import { isProduction } from '@upright/common/environment';
import { useIsFullMaintenanceMode } from '@upright/ui/views/root/context/use-is-maintenance-mode';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isLegacyShellAllowedPath } from './legacy-shell-allowlist';
import { useUnifiedShell } from './use-unified-shell';

/**
 * Reports the legacy chrome rendering for a user in the unified shell on a route nobody decided may
 * keep it (PLT-6596). Called by the two legacy components themselves, `BouToolHeader` and
 * `BouPageWithFrame`, rather than by the routes that pick them: a route added later, an error page
 * or a loading fallback reaches them without passing any switch a route-level check could watch.
 *
 * It logs instead of throwing. Throwing would take the page down for exactly the user the check is
 * meant to protect, and a team account with the shell's View setting on meets every known leak, so
 * the report has to be survivable. Production stays quiet: the console there has no reader, and
 * the CI gate for the same rule is `legacy-shell-guard.spec.ts`.
 *
 * Paths the frame is expected on are in `legacy-shell-allowlist.ts`; the one app-wide state is
 * full maintenance mode (`LEGACY_FRAME_ALLOWED_STATES.fullMaintenance`).
 */
export function useReportLegacyShellLeak(component: 'BouToolHeader' | 'BouPageWithFrame') {
  const { enabled } = useUnifiedShell();
  const fullMaintenance = useIsFullMaintenanceMode();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled || fullMaintenance || isProduction() || isLegacyShellAllowedPath(pathname)) return;
    console.error(
      `[unified shell] ${component} rendered on ${pathname} for a user in the unified shell. ` +
        'Move the view into the unified frame, or add the route to LEGACY_FRAME_ALLOWLIST ' +
        '(views/unified-shell/legacy-shell-allowlist.ts) with the decision behind it.',
    );
  }, [component, enabled, fullMaintenance, pathname]);
}
